import { Country, State } from "~/entities/Response/FormData";
import { Details } from "~/entities/Details";
import mapState from "~/assets/utils/mapState";

// BIK-2140: Ship1 resolves a detail's country/state against the allowed lists, which arrive
// async from Search.fetch(). Every path that builds a Details (saved address, event address,
// Google Places, region paste) used to hand-roll the same two lookups, and every one of them
// degraded silently the same way: with an empty list the match fails and the raw string
// ("US"/"TX") is left in place of the option object. The string has no `.code`, so the Rates
// payload ends up with `countryCode: ""` and the API rejects the whole request.
//
// This is the single implementation those call sites share. It is deliberately dependency-
// injected (no `this`, no Vuex, no Vue) so it can be called from components and from plain
// entities alike.

export interface ResolveResult {
  /** country is now a Country option object (either already was, or we just matched it) */
  countryResolved: boolean;
  /** state is now a State option object */
  stateResolved: boolean;
  /** at least one field was actually reassigned by this call */
  changed: boolean;
}

/** Case-insensitive, trimmed comparison of two option keys. */
function sameKey(a: unknown, b: unknown): boolean {
  if (a === null || a === undefined || b === null || b === undefined)
    return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/**
 * BIK-2144: is this an option object that is present but not USABLE by the selects?
 *
 * The region-paste path in avsCheck seeds an empty placeholder ({ code: "", id: "", name: "" })
 * so updateDetails has somewhere to deposit the UPS-parsed values. UPS answers with the CODE
 * only — `id`/`name` are dropdown fields, not API fields — so the detail ends up holding
 * { active: true, code: "US", id: "", name: "" }. That is a real country by `code`, which is
 * why AVS and the rates payload work fine, but SingleSelect renders `use-id` off `.id` and so
 * the Country field displays blank.
 *
 * Treat "has a code we can match, but no id/name to display" as still needing resolution, so
 * the re-resolve pass swaps in the full option object from allowedCountries.
 */
function isPartialCountry(value: unknown): boolean {
  if (!value || typeof value === "string") return false;
  const country = value as Partial<Country>;
  // Nothing to match on — a truly empty placeholder. Leave it alone: there is no signal
  // here, and inventing one is what BIK-2136/2137 had to undo.
  if (!country.code) return false;
  return !country.id || !country.name;
}

/** BIK-2144: same idea for a state — matchable by code, but missing its display fields. */
function isPartialState(value: unknown): boolean {
  if (!value || typeof value === "string") return false;
  const state = value as Partial<State> & { id?: string; label?: string };
  if (!state.stateprovcode) return false;
  return !state.id || !state.stateprov;
}

/**
 * Does this detail still carry an unresolved country or state?
 * Used to decide whether a re-resolve is worth running at all — a detail whose fields are
 * already usable option objects, or that the user picked by hand, must be left alone.
 *
 * BIK-2144: also true for a PARTIAL object (see isPartialCountry). Previously this only
 * caught raw strings, so the placeholder above was considered settled and nothing ever
 * repaired it — the field stayed visibly blank for the rest of the session.
 */
export function isUnresolvedDetail(detail: Details | null | undefined): boolean {
  if (!detail) return false;
  return (
    (!!detail.country && typeof detail.country === "string") ||
    (!!detail.state && typeof detail.state === "string") ||
    isPartialCountry(detail.country) ||
    isPartialState(detail.state)
  );
}

/**
 * Are both allowed lists populated enough to resolve against?
 * Single source of truth for "can I resolve yet?" — used by the prefill gate and by the
 * watcher that re-resolves once the lists land. Note `states` is null in the store until its
 * request resolves, and an explicit null overrides a Vue 2 prop default, so it must be
 * treated as not-ready rather than assumed to be an array.
 */
export function listsAreReady(
  allowedCountries: Country[] | null | undefined,
  states: State[] | null | undefined
): boolean {
  return !!allowedCountries?.length && !!states?.length;
}

/**
 * Turn a detail's string country/state into the full option objects the selects and the
 * submit payload expect. Mutates `detail` in place and reports what changed.
 *
 * Takes the FLAT states list and maps it internally, because every call site used to repeat
 * that `mapState(...)` call (and most of them forgot that mapState returns `false` on error).
 *
 * Assignment happens ONLY on a match. A no-match is a deliberate no-op: some countries have
 * no states list at all and legitimately carry a free-text state, and overwriting that with
 * `undefined` would lose the user's data.
 */
export default function resolveStateCountryObjects(
  detail: Details | null | undefined,
  allowedCountries: Country[] | null | undefined,
  states: State[] | null | undefined
): ResolveResult {
  const result: ResolveResult = {
    countryResolved: false,
    stateResolved: false,
    changed: false,
  };
  if (!detail) return result;

  // --- country ---------------------------------------------------------------
  // Match on code OR name: a saved address may carry either "US" or "United States"
  // (mirrors the predicate reverseDetailsComponents already used).
  // BIK-2144: an object is only "already resolved" if it is USABLE — it must carry the
  // id/name the selects render. A partial object (code only) falls through to the same
  // lookup a raw string takes, keyed on its code.
  const countryIsObject =
    !!detail.country &&
    typeof detail.country !== "string" &&
    !isPartialCountry(detail.country);
  const countryKey =
    typeof detail.country === "string"
      ? detail.country
      : (detail.country as Country)?.code;

  if (countryIsObject) {
    result.countryResolved = true;
  } else if (countryKey && allowedCountries?.length) {
    const matchedCountry = allowedCountries.find(
      (c: Country) => sameKey(c?.code, countryKey) || sameKey(c?.name, countryKey)
    );
    if (matchedCountry) {
      detail.country = matchedCountry;
      result.countryResolved = true;
      result.changed = true;
    }
  }

  // --- state -----------------------------------------------------------------
  // Only resolvable once the country is known, since the states map is keyed by country code.
  // BIK-2144: same usability test as country above.
  const stateIsObject =
    !!detail.state &&
    typeof detail.state !== "string" &&
    !isPartialState(detail.state);
  const stateKey =
    typeof detail.state === "string"
      ? detail.state
      : (detail.state as State)?.stateprovcode;
  const resolvedCountryCode =
    typeof detail.country === "string"
      ? detail.country
      : (detail.country as Country)?.code;

  if (stateIsObject) {
    result.stateResolved = true;
  } else if (stateKey && resolvedCountryCode && states?.length) {
    // mapState returns `false` on error and uses an array as a string-keyed map, so guard
    // the shape rather than testing `.length`.
    const allowedStates = mapState(states as State[]);
    const countryStates = allowedStates
      ? (allowedStates as any)[resolvedCountryCode]
      : null;
    if (countryStates?.length) {
      const matchedState = countryStates.find(
        (s: State) =>
          sameKey(s?.stateprovcode, stateKey) || sameKey(s?.stateprov, stateKey)
      );
      if (matchedState) {
        detail.state = matchedState;
        result.stateResolved = true;
        result.changed = true;
      }
    }
  }

  return result;
}
